import {
  createCourseRazorpayOrder,
  verifyCourseRazorpayPayment,
  enrollFreeCourse,
} from '../api';

export const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/** Clear message after POST /course-verify (axios error). */
export function formatRazorpayVerifyError(err) {
  const status = err?.response?.status;
  const dataMsg = err?.response?.data?.message;
  if (status === 401) {
    return 'Your session expired during checkout. Log in again, then complete payment.';
  }
  if (dataMsg === 'Payment signature verification failed') {
    return 'We could not confirm the payment with our server. If you were charged, contact support. The team should verify Razorpay Key ID and Key Secret in backend .env (same pair, no spaces).';
  }
  return dataMsg || err?.message || 'Payment verification failed. Please contact support.';
}

const numericCoursePrice = (course) => {
  const n = Number(String(course?.price ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

/**
 * Free course (price 0): enroll-free API + local enrollment.
 * Paid course: Razorpay checkout; on success verifies payment and refreshes user.
 * Resolves `{ ok: true }` or `{ dismissed: true }` if user closes the modal without paying.
 */
export function purchaseOrEnrollCourse({ course, user, refreshUser, enrollInCourse }) {
  if (!course?.id || !user?.id) {
    return Promise.reject(new Error('Missing course or user'));
  }

  const price = numericCoursePrice(course);
  const isPaidCourse = Number.isFinite(price) && price > 0;
  const isIncludedInSubscription = (user?.subscriptionFreeCourseIds || []).map(String).includes(String(course.id));

  if (!isPaidCourse || isIncludedInSubscription) {
    const promise = isIncludedInSubscription ? Promise.resolve() : enrollFreeCourse(course.id);
    return promise
      .then(() => refreshUser())
      .then(() => {
        enrollInCourse(user.id, course.id);
        return { ok: true };
      });
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let paymentHandled = false;

    const finish = (fn) => {
      if (settled) return;
      settled = true;
      fn();
    };

    (async () => {
      try {
        const scriptOk = await loadRazorpayScript();
        if (!scriptOk) {
          finish(() => reject(new Error('Unable to load Razorpay. Please check your internet and try again.')));
          return;
        }

        const order = await createCourseRazorpayOrder(course.id);
        if (!order?.orderId || !order?.keyId) {
          finish(() => reject(new Error('Unable to create payment order. Please try again.')));
          return;
        }

        const rz = new window.Razorpay({
          key: order.keyId,
          amount: Number(order.amount),
          currency: order.currency || 'INR',
          name: 'Market Mavericks',
          description: `Course: ${course.title || 'Course'}`,
          order_id: order.orderId,
          prefill: {
            name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            email: user.email || '',
            contact: user.mobile || user.number || '',
          },
          theme: { color: '#38915c' },
          handler: async function (response) {
            paymentHandled = true;
            try {
              await verifyCourseRazorpayPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              await refreshUser();
              enrollInCourse(user.id, course.id);
              finish(() => resolve({ ok: true }));
            } catch (verifyErr) {
              finish(() => reject(new Error(formatRazorpayVerifyError(verifyErr))));
            }
          },
          modal: {
            ondismiss: () => {
              if (paymentHandled) return;
              finish(() => resolve({ dismissed: true }));
            },
          },
        });

        rz.on('payment.failed', (resp) => {
          const e = resp?.error || {};
          const detail = [e.description, e.reason, e.step].filter(Boolean).join(' — ');
          finish(() =>
            reject(new Error(detail || 'Payment failed. For test mode use Razorpay test cards only.'))
          );
        });

        rz.open();
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || 'Unable to start payment';
        finish(() => reject(new Error(msg)));
      }
    })();
  });
}
